import _ from "lodash";
import update from "immutability-helper";
import { generateRandomIdV2, localSearch, createRegex } from "Utils";
import ReactDOMServer from "react-dom/server";
import React from "react";

export const columnIds = {
  label: "LABEL",
  descriptor: "DESCRIPTOR",
  icon: "ICON",
  grades: "GRADES",
  code: "CODE",
  tags: "TAGS",
  selectableLabel: "SELECTABLE_LABEL",
  previewSelectableLabel: "PREVIEW_SELECTABLE_LABEL",
};

export const bulkUpdateColumnIds = {
  grades: "GRADES",
  tags: "TAGS",
};

export const filterIds = {
  searchText: "SEARCH_TEXT",
  grades: "GRADES",
  tags: "TAGS",
};

export const MANUAL_SCROLL = "MANUAL_SCROLL";

export const AUTOMATIC_SCROLL = "AUTOMATIC_SCROLL";

export const getFilterObjectBasedOnAllowedFilters = ({
  allowedFilters,
  filtersInitialValue,
}) => {
  return _.reduce(
    allowedFilters,
    (result, filter) => {
      switch (filter) {
        case filterIds.searchText: {
          return { ...result, [filter]: "" };
        }
        default: {
          const initialValue = filtersInitialValue[filter];

          return {
            ...result,
            [filter]: initialValue ? initialValue : [],
          };
        }
      }
    },
    {}
  );
};

export const isAnyFilterSelected = ({ filters }) => {
  const filterKeys = _.keys(filters);

  return _.some(filterKeys, filterKey => {
    return !_.isEmpty(filters[filterKey]);
  });
};

export const convertNodesMappingToNodesArray = ({ nodes, rootNode }) => {
  const { children } = rootNode;

  return _.flatMap(children, childId => {
    const node = nodes[childId];
    if (_.isEmpty(node)) return [];

    const updatedNode = _.omit(node, ["isError"]);
    const { children = [] } = updatedNode;

    if (_.size(children) > 0) {
      return [
        updatedNode,
        ...convertNodesMappingToNodesArray({
          nodes,
          rootNode: updatedNode,
        }),
      ];
    }

    return updatedNode;
  });
};

export const generateEmptyNodes = ({ countOfNodes, rootNode, nodes }) => {
  let updatedNodes = { ...nodes };
  let updatedRootNode = { ...rootNode };

  for (let i = 0; i < countOfNodes; i++) {
    const newNode = {
      id: "NEW_" + `${generateRandomIdV2()}_` + Date.now(),
      label: "",
      parentId: rootNode.id,
      children: [],
      depth: 1,
      code: "",
      grades: [],
      tags: [],
      isNew: true,
    };

    updatedNodes = update(updatedNodes, {
      [newNode.id]: { $set: newNode },
    });

    updatedRootNode = update(updatedRootNode, {
      children: { $unshift: [newNode.id] },
    });
  }

  return { updatedRootNode, updatedNodes };
};

export const getSelectedStandardsCount = ({ nodes }) => {
  const count = _.reduce(
    nodes,
    (result, value, key) => {
      const isChecked = _.get(value, "isChecked", false);
      const nodeId = _.get(value, "id", false);
      if (isChecked) {
        result[1].push(nodeId);
        return [result[0] + 1, result[1], result[2]];
      } else {
        return result;
      }
    },
    [0, [], []]
  );
  return count;
};

export const getChildPosition = ({ nodes, childId }) => {
  const parentId = nodes[childId].parentId;
  const parentNode = nodes[parentId];
  const childIndex = _.findIndex(parentNode.children, id => id == childId);
  return childIndex;
};

export const addChildrenRecursively = ({ nodes, nodeId, resultArray }) => {
  //1) push this nodeId to the resultArray
  resultArray.push(nodeId);
  //2) run this function for its children
  const node = nodes[nodeId];
  if (!_.isEmpty(node.children)) {
    for (const childNodeId of node.children) {
      addChildrenRecursively({
        nodes,
        nodeId: childNodeId,
        resultArray,
      });
    }
  }
};

export const onDeleteClick = ({
  nodes,
  setNodes,
  selectedNodeIds,
  rootNodeId,
}) => {
  //1) remove these ids from children array of their parent and store all the parentIds
  let updatedNodes = { ...nodes };
  const updatedParentNodes = {};

  for (const nodeId of selectedNodeIds) {
    const parentId = updatedNodes[nodeId].parentId;
    const parentNode = updatedNodes[parentId];
    const childPosition = getChildPosition({
      nodes: updatedNodes,
      childId: nodeId,
    });

    const updatedParentNode = update(parentNode, {
      children: {
        $splice: [[childPosition, 1]],
      },
    });

    updatedNodes[parentId] = updatedParentNode;
    updatedParentNodes[parentId] = updatedParentNode;
  }

  //2)for each of these selectedNodeIds find their children recursively and add their ids in an array
  const nodeIdsToBeDeleted = [];
  for (const nodeId of selectedNodeIds) {
    addChildrenRecursively({
      nodes: updatedNodes,
      nodeId,
      resultArray: nodeIdsToBeDeleted,
    });
  }
  //3) calculate final updated parent nodes
  _.forOwn(updatedParentNodes, (value, key) => {
    if (_.includes(nodeIdsToBeDeleted, key)) {
      delete updatedParentNodes[key];
    }
  });
  //4) update the local state
  _.forOwn(updatedNodes, (value, key) => {
    if (_.includes(nodeIdsToBeDeleted, key)) {
      delete updatedNodes[key];
    }
  });

  if (_.size(updatedNodes) === 1) {
    // i.e There is only 1 node i.e it contains only root node
    updatedNodes = onAddFirstRow({
      rootNodeId,
      nodes: updatedNodes,
      setNodes,
    });
    const inputForMutation = {
      updatedNodes,
      removedNodes: nodeIdsToBeDeleted,
    };
    return inputForMutation;
  } else {
    setNodes(updatedNodes);
    const inputForMutation = {
      updatedNodes: [..._.values(updatedParentNodes)],
      removedNodes: nodeIdsToBeDeleted,
    };
    return inputForMutation;
  }
};

//we click on add new row button on table header, we should add it as a first child
export const onAddFirstRow = ({
  rootNodeId,
  nodes,
  setNodes,
  userId,
  insertRootNode = true,
}) => {
  const newNodes = { ...nodes };
  const rootNode = newNodes[rootNodeId];
  // create a new node
  const newNode = {
    id: "NEW_" + Date.now() + `${userId}`,
    label: "",
    parentId: insertRootNode ? rootNodeId : null,
    children: [],
    depth: 1,
    code: "",
    grades: [],
    tags: [],
    isNew: true,
  };

  //insert it in first position of rootNode's children array
  const updatedRootNode = update(rootNode, {
    children: {
      $unshift: [newNode.id],
    },
  });

  newNodes[rootNodeId] = updatedRootNode;
  newNodes[newNode.id] = newNode;
  //finally update the state
  setNodes(newNodes);
  const updatedNodesForMutation = insertRootNode
    ? [updatedRootNode, newNode]
    : [newNode];

  return updatedNodesForMutation;
};

export const onAddLastRow = ({ rootNodeId, nodes, setNodes }) => {
  const updatedNodes = { ...nodes };
  const rootNode = updatedNodes[rootNodeId];

  //Create a new node
  const newNode = {
    id: "NEW_" + Date.now(),
    label: "",
    parentId: rootNodeId,
    children: [],
    depth: 1,
    code: "",
    grades: [],
    tags: [],
    isNew: true,
  };

  const updatedRootNode = update(rootNode, {
    children: {
      $push: [newNode.id],
    },
  });

  updatedNodes[rootNodeId] = updatedRootNode;
  updatedNodes[newNode.id] = newNode;

  setNodes(updatedNodes);

  return newNode;
};

export const onAddNewNode = ({
  nodes,
  setNodes,
  nodeId,
  isGradeFilterActive,
  isTagsFilterActive,
}) => {
  const sourceNode = nodes[nodeId];
  let sourceParentNode = nodes[sourceNode.parentId];
  const { grades, tags } = sourceNode;
  // create a new node
  const newNode = {
    id: "NEW_" + Date.now(),
    label: "",
    parentId: sourceNode.parentId,
    children: [],
    depth: sourceNode.depth,
    code: "",
    grades: isGradeFilterActive ? _.cloneDeep(grades) : [], // add sources node grades and tags if filters are selected
    tags: isTagsFilterActive ? _.cloneDeep(tags) : [],
    isNew: true,
  };
  //insert it in a position after the sourceNode in the sourceNode's parentId's children array
  const sourceChildPosition = getChildPosition({
    nodes,
    childId: sourceNode.id,
  });

  sourceParentNode = update(sourceParentNode, {
    children: {
      $splice: [[sourceChildPosition + 1, 0, newNode.id]],
    },
  });

  //finally update the state
  setNodes(prevNodes => {
    return {
      ...prevNodes,
      [sourceParentNode.id]: sourceParentNode,
      [newNode.id]: newNode,
    };
  });
  const updatedNodesForMutation = [sourceParentNode, newNode];
  return [updatedNodesForMutation, newNode.id];
};

export const getParentDepthDifference = ({ nodes, nodeId }) => {
  const node = nodes[nodeId];
  const parentNode = nodes[node.parentId];
  return node.depth - parentNode.depth;
};

export const updateNode = ({ setNodes, nodeId, params = {} }) => {
  setNodes(prevNodes => {
    return {
      ...prevNodes,
      [nodeId]: {
        ...prevNodes[nodeId],
        ...params,
      },
    };
  });
};

//Bulk rows that are copied from google docs excel sheet are being paste into NodeEditor.js.
export const pasteBulkBenchmarkItems = ({
  nodes,
  setNodes,
  nodeId,
  cellName,
  isNewRow = true,
  itemsList,
  isGradeFilterActive,
  isTagsFilterActive,
}) => {
  const newNodes = { ...nodes };
  const currentNode = { ...newNodes[nodeId] };
  const itemsListLength = _.size(itemsList);
  const updatedNodes = [];
  const currentNodeParentId = currentNode.parentId;
  const { grades, tags } = currentNode;
  let parentNode = { ...newNodes[currentNodeParentId] };

  const parentNodeChildrenIds = parentNode.children;
  const currentNodeIndex = getChildPosition({ nodes, childId: nodeId });

  if (isNewRow) {
    currentNode[cellName] = _.head(itemsList); // add data in the current column
    newNodes[nodeId] = currentNode;
    updatedNodes.push(currentNode);

    const appendChildIds = [];
    for (let i = 0; i < itemsListLength - 1; i++) {
      const newNode = {
        id: "NEW_" + `${generateRandomIdV2()}`, //for unique id
        [cellName]: itemsList[i + 1],
        parentId: currentNodeParentId,
        children: [],
        depth: currentNode.depth,
        code: "",
        grades: isGradeFilterActive ? _.cloneDeep(grades) : [], // add sources node grades and tags if filters are selected
        tags: isTagsFilterActive ? _.cloneDeep(tags) : [],
        isNew: true,
      };

      const newNodeId = newNode.id;
      updatedNodes.push(newNode);
      newNodes[newNodeId] = newNode;
      appendChildIds.push(newNodeId);
    }
    // append new nodes child ID's
    parentNode = update(parentNode, {
      children: {
        $splice: [[currentNodeIndex + 1, 0, ...appendChildIds]],
      },
    });

    newNodes[currentNodeParentId] = parentNode;
    updatedNodes.push(parentNode);
    setNodes(newNodes);
  } else {
    // The data will be overwritten on current nodes column i.e no new nodes will be created
    for (
      let i = currentNodeIndex, j = 0;
      i < _.size(parentNodeChildrenIds);
      i++, j++
    ) {
      if (j === itemsListLength) break;
      const currentNode = { ...newNodes[parentNodeChildrenIds[i]] };
      currentNode[cellName] = itemsList[j];

      newNodes[parentNodeChildrenIds[i]] = currentNode;
      updatedNodes.push(currentNode);
    }

    setNodes(newNodes);
  }

  return updatedNodes;
};

export const onUpdateNodeIcon = ({ setNodes, nodeId, nodes, icon }) => {
  const newNodes = { ...nodes };
  const updatedNodes = [];
  const updatedCurrentNode = update(newNodes[nodeId], {
    icon: {
      $set: icon,
    },
  });

  newNodes[nodeId] = updatedCurrentNode;
  updatedNodes.push(updatedCurrentNode);

  setNodes(newNodes);
  return updatedNodes;
};

export const handleDragAndDrop = ({
  result,
  nodes,
  setNodes,
  indexCountArray,
}) => {
  const newNodes = { ...nodes };

  const nodeId = result.draggableId;

  const sourceNode = { ...newNodes[nodeId] };

  const sourceIndex = result.source.index + 1;
  const destinationIndex = result.destination && result.destination.index + 1;

  const destinationNodeId = indexCountArray[destinationIndex - 1];
  let destinationNode = { ...newNodes[destinationNodeId] };
  if (destinationNode) {
    const dragDirection = destinationIndex - sourceIndex > 0 ? "down" : "up";
    //compare depths of both the nodes. both nodes should be of same depth
    if (
      sourceNode.depth == destinationNode.depth &&
      sourceIndex != destinationIndex &&
      ((_.isEmpty(destinationNode.children) && dragDirection == "down") ||
        dragDirection == "up")
    ) {
      //the item can be drag and dropped. we follow these steps to change the data
      //1) remove this dragged node from its parentId's children array
      let sourceParentNode = { ...newNodes[sourceNode.parentId] };
      const sourceChildPosition = getChildPosition({
        nodes: newNodes,
        childId: sourceNode.id,
      });

      sourceParentNode = update(sourceParentNode, {
        children: {
          $splice: [[sourceChildPosition, 1]],
        },
      });
      newNodes[sourceParentNode.id] = sourceParentNode;

      //2) set this dragged node's parentId to the destination node's parentId
      sourceNode.parentId = destinationNode.parentId;
      newNodes[sourceNode.id] = sourceNode;

      //3) insert this node's id in the children element of the destination node's parentId a position after the destination node if the drag direction is down and vice versa
      let destinationParentNode = { ...newNodes[destinationNode.parentId] };
      const destinationChildPosition = getChildPosition({
        nodes: newNodes,
        childId: destinationNode.id,
      });

      if (dragDirection == "down") {
        destinationParentNode = update(destinationParentNode, {
          children: {
            $splice: [[destinationChildPosition + 1, 0, sourceNode.id]],
          },
        });
      } else if (dragDirection == "up") {
        destinationParentNode = update(destinationParentNode, {
          children: {
            $splice: [[destinationChildPosition, 0, sourceNode.id]],
          },
        });
      }
      newNodes[destinationParentNode.id] = destinationParentNode;

      //4 finally update the state with all these changes
      setNodes(newNodes);

      const updatedNodesForMutation = [
        sourceParentNode,
        sourceNode,
        destinationParentNode,
      ];
      return updatedNodesForMutation;
    } else if (
      sourceNode.depth - destinationNode.depth == 1 &&
      dragDirection == "down"
    ) {
      //1) remove this node from its parentId's children
      let sourceParentNode = { ...newNodes[sourceNode.parentId] };
      const sourceChildPosition = getChildPosition({
        nodes: newNodes,
        childId: sourceNode.id,
      });

      sourceParentNode = update(sourceParentNode, {
        children: {
          $splice: [[sourceChildPosition, 1]],
        },
      });
      newNodes[sourceParentNode.id] = sourceParentNode;

      //2) update this node's parentId as destionation node's id
      sourceNode.parentId = destinationNode.id;
      newNodes[sourceNode.id] = sourceNode;

      //3) append this node's id as a first child id of the destination node
      destinationNode = update(destinationNode, {
        children: {
          $unshift: [sourceNode.id],
        },
      });
      newNodes[destinationNode.id] = destinationNode;

      //4 finally update the state with all these changes
      setNodes(newNodes);

      const updatedNodesForMutation = [
        sourceParentNode,
        sourceNode,
        destinationNode,
      ];
      return updatedNodesForMutation;
    } else if (
      sourceNode.depth - destinationNode.depth >= 1 &&
      dragDirection == "up" &&
      !(
        newNodes[destinationNode.parentId].depth == 0 &&
        newNodes[destinationNode.parentId].children[0] == destinationNode.id
      )
    ) {
      //1) find the node which is just above destination node.

      let nodeJustAboveDestination = {
        ...newNodes[indexCountArray[destinationIndex - 2]],
      };

      //2) if it is of same level, make this node as chldren of its parentId
      if (sourceNode.depth == nodeJustAboveDestination.depth) {
        //1) remove source node from its parentId's children
        let sourceParentNode = { ...newNodes[sourceNode.parentId] };
        const sourceChildPosition = getChildPosition({
          nodes: newNodes,
          childId: sourceNode.id,
        });

        sourceParentNode = update(sourceParentNode, {
          children: {
            $splice: [[sourceChildPosition, 1]],
          },
        });
        newNodes[sourceParentNode.id] = sourceParentNode;

        //2) make the parentId of source Node as nodeJustAboveDestination's parentId
        sourceNode.parentId = nodeJustAboveDestination.parentId;
        newNodes[sourceNode.id] = sourceNode;

        //3) insert our sourceNode in a position after nodeJustAboveDestination in its parentId
        let nodeJustAboveDestinationParent = {
          ...newNodes[nodeJustAboveDestination.parentId],
        };

        const nodeJustAboveChildPosition = getChildPosition({
          nodes: newNodes,
          childId: nodeJustAboveDestination.id,
        });

        nodeJustAboveDestinationParent = update(
          nodeJustAboveDestinationParent,
          {
            children: {
              $splice: [[nodeJustAboveChildPosition + 1, 0, sourceNode.id]],
            },
          }
        );
        newNodes[
          nodeJustAboveDestinationParent.id
        ] = nodeJustAboveDestinationParent;

        //finally update all these data
        setNodes(newNodes);

        const updatedNodesForMutation = [
          sourceParentNode,
          sourceNode,
          nodeJustAboveDestinationParent,
        ];
        return updatedNodesForMutation;
      }
      //if the node just above destination node is of one level higher? then make this node as its children
      else if (
        sourceNode.depth - nodeJustAboveDestination.depth == 1 &&
        _.isEmpty(nodeJustAboveDestination.children)
      ) {
        //1) remove source node from its parentId's children
        let sourceParentNode = { ...newNodes[sourceNode.parentId] };
        const sourceChildPosition = getChildPosition({
          nodes: newNodes,
          childId: sourceNode.id,
        });

        sourceParentNode = update(sourceParentNode, {
          children: {
            $splice: [[sourceChildPosition, 1]],
          },
        });
        newNodes[sourceParentNode.id] = sourceParentNode;

        //2) make the nodeJustAboveDestination as its new parentId
        sourceNode.parentId = nodeJustAboveDestination.id;
        newNodes[sourceNode.id] = sourceNode;

        //3) push this node as the nodeJustAboveDestination's children
        nodeJustAboveDestination = update(nodeJustAboveDestination, {
          children: {
            $push: [sourceNode.id],
          },
        });
        newNodes[nodeJustAboveDestination.id] = nodeJustAboveDestination;

        //4) Finally update all this data
        setNodes(newNodes);

        const updatedNodesForMutation = [
          sourceParentNode,
          sourceNode,
          nodeJustAboveDestination,
        ];
        return updatedNodesForMutation;
      } else if (nodeJustAboveDestination.depth - sourceNode.depth == 1) {
        //1) remove source node from its parentId's children
        let sourceParentNode = { ...newNodes[sourceNode.parentId] };
        const sourceChildPosition = getChildPosition({
          nodes: newNodes,
          childId: sourceNode.id,
        });

        sourceParentNode = update(sourceParentNode, {
          children: {
            $splice: [[sourceChildPosition, 1]],
          },
        });
        newNodes[sourceParentNode.id] = sourceParentNode;

        //2) make the parentId as grand parentId of the nodeJustAboveDestination
        sourceNode.parentId =
          newNodes[nodeJustAboveDestination.parentId].parentId;
        newNodes[sourceNode.id] = sourceNode;

        //3) make this source ndoe as the last child of the grandparent node
        let grandParentNode = {
          ...newNodes[newNodes[nodeJustAboveDestination.parentId].parentId],
        };

        grandParentNode = update(grandParentNode, {
          children: {
            $push: [sourceNode.id],
          },
        });
        newNodes[grandParentNode.id] = grandParentNode;

        //4) finally update all this data
        setNodes(newNodes);

        const updatedNodesForMutation = [
          sourceParentNode,
          sourceNode,
          grandParentNode,
        ];
        return updatedNodesForMutation;
      }
    } else if (
      destinationNode.depth - sourceNode.depth >= 1 &&
      dragDirection == "down"
    ) {
      //1) check if the destination is not sourceNode's child, and it is the last child of its parentId
      if (
        destinationNode.parentId != sourceNode.id &&
        newNodes[destinationNode.parentId].children[
          newNodes[destinationNode.parentId].children.length - 1
        ] == destinationNode.id
      ) {
        //2) if yes, then
        //3) iterate through its parents until we find same level parentId as our sourceNode
        let destinationNodeParent = newNodes[destinationNode.parentId];
        while (destinationNodeParent.depth != sourceNode.depth) {
          destinationNodeParent = newNodes[destinationNodeParent.parentId];
        }

        //4) remove sourceNode from its parentId, and make its parentId as this newly found node's
        let sourceParentNode = { ...newNodes[sourceNode.parentId] };
        const sourceChildPosition = getChildPosition({
          nodes: newNodes,
          childId: sourceNode.id,
        });

        sourceParentNode = update(sourceParentNode, {
          children: {
            $splice: [[sourceChildPosition, 1]],
          },
        });
        newNodes[sourceParentNode.id] = sourceParentNode;

        sourceNode.parentId = destinationNodeParent.parentId;
        newNodes[sourceNode.id] = sourceNode;

        //5) go to the newly found node's parentId and append this node's id in its children array
        let newlyFoundNodeParent = {
          ...newNodes[destinationNodeParent.parentId],
        };
        const destinationNodeParentChildPosition = getChildPosition({
          nodes: newNodes,
          childId: destinationNodeParent.id,
        });

        newlyFoundNodeParent = update(newlyFoundNodeParent, {
          children: {
            $splice: [
              [destinationNodeParentChildPosition + 1, 0, sourceNode.id],
            ],
          },
        });
        newNodes[newlyFoundNodeParent.id] = newlyFoundNodeParent;

        //finally update the state with all this data
        setNodes(newNodes);

        const updatedNodesForMutation = [
          sourceParentNode,
          sourceNode,
          newlyFoundNodeParent,
        ];
        return updatedNodesForMutation;
      }
    }
  }
};

//This func always return Bool
export const shouldNodeRender = ({ filteredNodeIds, rootNodeId }) => {
  return rootNodeId in filteredNodeIds;
};

/**
 * Scenarios for which left indenting will be disabled :-
 * - any of the filter is active
 * - if the node is of depth 1
 * - if the node has children
 * - if the difference of depth of this node and its parent node is not 1
 */
export const getIsLeftIndentationDisabled = ({
  nodes,
  nodeId,
  areFiltersActive,
}) => {
  const node = nodes[nodeId];

  if (
    areFiltersActive ||
    node.depth == 1 ||
    !_.isEmpty(node.children) ||
    getParentDepthDifference({ nodes, nodeId }) !== 1
  ) {
    return true;
  }
};

/**
 * Process of left indenting a node
 *     - decrease the depth of this node by 1
 *     - update current node's parentId to its grand parentId
 *     - update current node's parentId's children. (chop off children, from current node and rest of all including current node)
 *     - update current node's children to those remaining children
 *     - update the remaining children's parentId to current node's id
 *     - insert current node's Id at a position after its parentId in its grand parentId's children array
 *     - update local state with all this data
 */
export const onLeftIndentNode = ({ nodes, setNodes, nodeId }) => {
  const childPosition = getChildPosition({
    nodes,
    childId: nodeId,
  });
  let updatedNode = { ...nodes[nodeId] };
  let parentNode = { ...nodes[updatedNode.parentId] };
  let grandParentNode = { ...nodes[parentNode.parentId] };

  updatedNode = update(updatedNode, {
    depth: {
      $set: updatedNode.depth - 1,
    },
    parentId: {
      $set: grandParentNode.id,
    },
  });

  const remainingChildren = _.slice(
    parentNode.children,
    childPosition + 1,
    Infinity
  );

  parentNode = update(parentNode, {
    children: {
      $splice: [[childPosition, Infinity]],
    },
  });

  updatedNode = update(updatedNode, {
    children: {
      $set: remainingChildren,
    },
  });

  const updatedNodes = {
    ...nodes,
    [updatedNode.id]: updatedNode,
    [parentNode.id]: parentNode,
  };

  const childNodes = {};

  for (const childId of updatedNode.children) {
    const childNode = { ...updatedNodes[childId] };
    childNode.parentId = updatedNode.id;

    updatedNodes[childId] = childNode;
    childNodes[childId] = childNode;
  }

  const parentsPosition = getChildPosition({
    nodes: updatedNodes,
    childId: parentNode.id,
  });

  grandParentNode = update(grandParentNode, {
    children: {
      $splice: [[parentsPosition + 1, 0, updatedNode.id]],
    },
  });

  updatedNodes[grandParentNode.id] = grandParentNode;

  setNodes(updatedNodes);
  return [grandParentNode, parentNode, updatedNode, ..._.values(childNodes)];
};

/**
 * On left indenting a node, its children are updated.
 * So, we make grades of node equal to union of its grades and its children's grades
 */
export const onLeftIndentGrades = ({ nodes, setNodes, nodeId }) => {
  const newNodes = _.keyBy(nodes, "id");
  let currentNode = { ...newNodes[nodeId] };
  const currentNodeGrades = currentNode.grades;

  let updateParentNodeGrades = [...currentNodeGrades];
  for (const childId of currentNode.children) {
    const childNode = newNodes[childId];

    const childNodeGrades = childNode.grades;
    const filterGrades = _.unionBy(
      childNodeGrades,
      updateParentNodeGrades,
      "id"
    );

    updateParentNodeGrades = filterGrades;
  }

  currentNode = update(currentNode, {
    grades: {
      $set: updateParentNodeGrades,
    },
  });

  newNodes[nodeId] = currentNode;

  setNodes(prevNodes => {
    return {
      ...prevNodes,
      ...newNodes,
    };
  });
};
/**
 * Scenarios for which right indenting will be disabled :-
 * - any of the filter is active
 * - if node depth is equal to maxDepth
 * - if the node is first child of its parent
 * - if the difference of depth of this node and its parent node is not 1
 */
export const getIsRightIndentationDisabled = ({
  nodes,
  nodeId,
  maxDepth,
  areFiltersActive,
}) => {
  const node = nodes[nodeId];
  const isFirstChild = _.get(nodes, [node.parentId, "children", 0]) == nodeId;

  if (
    areFiltersActive ||
    node.depth == maxDepth ||
    isFirstChild ||
    getParentDepthDifference({ nodes, nodeId }) !== 1
  ) {
    return true;
  }
};

/**
 * Process of right indenting a node
 *     - increase the depth of this node by 1
 *     - current node's left sibling will be treated as parent node
 *     - append this node's id and all its children ids as an array at the end of parent node's children array
 *     - update parent id of all its children to new parent node's id
 *     - clear children of the current node
 *     - update current parent of the node. From its children, remove current node
 *     - update local state with all this data
 */

export const onRightIndentNode = ({ nodes, setNodes, nodeId }) => {
  const node = nodes[nodeId];

  const childPosition = getChildPosition({
    nodes,
    childId: nodeId,
  });

  let updatedNode = { ...nodes[nodeId] };
  let parentNode = { ...nodes[node.parentId] };
  const updatedParentNodeId = parentNode.children[childPosition - 1];
  let updatedParentNode = { ...nodes[updatedParentNodeId] };

  updatedNode = update(updatedNode, {
    depth: {
      $set: updatedNode.depth + 1,
    },
  });

  updatedParentNode = update(updatedParentNode, {
    children: {
      $set: _.union(
        updatedParentNode.children,
        [updatedNode.id],
        updatedNode.children
      ),
    },
  });

  updatedNode = update(updatedNode, {
    parentId: {
      $set: updatedParentNodeId,
    },
  });

  const updatedNodes = {
    ...nodes,
    [updatedNode.id]: updatedNode,
    [updatedParentNodeId]: updatedParentNode,
  };

  const childNodes = {};
  const updatedNodeChildren = _.get(updatedNode, "children", []) ?? [];

  _.forEach(updatedNodeChildren, childId => {
    const childNode = { ...updatedNodes[childId] };
    childNode.parentId = updatedParentNodeId;

    updatedNodes[childId] = childNode;
    childNodes[childId] = childNode;
  });

  updatedNode.children = [];
  updatedNodes[updatedNode.id] = updatedNode;

  parentNode = update(parentNode, {
    children: {
      $splice: [[childPosition, 1]],
    },
  });

  updatedNodes[parentNode.id] = parentNode;

  setNodes(updatedNodes);

  return [parentNode, updatedParentNode, updatedNode, ..._.values(childNodes)];
};

/**
 * On right indent of node, we make sure that its new parent contains all grades, which are present in node
 */

export const onRightIndentGrades = ({ nodes, setNodes, nodeId }) => {
  const newNodes = { ..._.keyBy(nodes, "id") };
  const node = newNodes[nodeId];
  const parentNode = { ...newNodes[node.parentId] };
  const updatedNodes = [];

  const currentNodeGrades = node["grades"];
  const parentNodeGrades = parentNode["grades"];

  const gradesCopyToParent = _.differenceBy(
    currentNodeGrades,
    parentNodeGrades,
    "id"
  );

  parentNode.grades = [...parentNode.grades, ...gradesCopyToParent];
  newNodes[parentNode.id] = parentNode;

  updatedNodes.push(parentNode);
  setNodes(nodes => {
    return {
      ...nodes,
      ...newNodes,
    };
  });
};

/**
 * When grades are updated,
 * - We update grades of current node
 * - Since any nodes grades are atleast union of its children's grades, we update grades of all its parents when grade is added
 * - Due to same reason, when grade is removed, we update grades of all its children and remove corresponding grades from its childrens
 */
export const onUpdateNodeGrades = ({ setNodes, nodeId, nodes, nodeGrades }) => {
  let newNodes = { ...nodes };

  const currentNode = { ...newNodes[nodeId] };
  const currentNodeParentId = currentNode.parentId;
  const currentNodeChildren = currentNode.children;

  const oldGrades = currentNode["grades"];
  currentNode["grades"] = [...nodeGrades];
  newNodes[nodeId] = currentNode;

  if (_.size(oldGrades) < _.size(nodeGrades)) {
    newNodes = _addNodeGradesUpwards({
      parentId: currentNodeParentId,
      filterGrades: nodeGrades,
      nodes: newNodes,
    });
  }

  if (_.size(oldGrades) > _.size(nodeGrades)) {
    newNodes = _removeNodeGradesDownwards({
      children: currentNodeChildren,
      filterGrades: nodeGrades,
      nodes: newNodes,
    });
  }

  setNodes(newNodes);
};

const _addNodeGradesUpwards = ({ parentId, filterGrades, nodes }) => {
  const depth = _.get(nodes, `[${parentId}].depth`, 0);

  const currentNodeGrades = nodes[parentId]["grades"];
  const updatedGrades = _.unionBy(filterGrades, currentNodeGrades, "id"); // grades which are not present in parent grades

  if (_.size(updatedGrades) > _.size(currentNodeGrades)) {
    nodes = update(nodes, {
      [parentId]: {
        grades: {
          $set: [...updatedGrades],
        },
      },
    });
  }

  if (depth === 0) return nodes;

  const currentNodeParentId = nodes[parentId]["parentId"];
  return _addNodeGradesUpwards({
    parentId: currentNodeParentId,
    filterGrades,
    nodes: nodes,
  });
};

const _removeNodeGradesDownwards = ({ children, filterGrades, nodes }) => {
  if (!children) return nodes;

  let updatedNodes = { ...nodes };

  for (const childId of children) {
    const childNode = { ...updatedNodes[childId] };
    const currentNodeGrades = _.get(childNode, "grades", []);

    const intersectionGrades = _.intersectionBy(
      filterGrades,
      currentNodeGrades,
      "id"
    );

    if (_.size(intersectionGrades) < _.size(currentNodeGrades)) {
      childNode["grades"] = intersectionGrades;
      updatedNodes[childId] = childNode;
    }

    updatedNodes = _removeNodeGradesDownwards({
      children: _.get(childNode, "children", []),
      filterGrades,
      nodes: updatedNodes,
    });
  }

  return updatedNodes;
};

export const onBulkAddGrades = ({
  nodes,
  setNodes,
  selectedNodeIds,
  itemsList,
}) => {
  let newNodes = { ...nodes };
  const updatedNodes = [];

  _.map(selectedNodeIds, nodeId => {
    const currentNode = { ...newNodes[nodeId] };
    const currentNodeParentId = currentNode.parentId;
    const currentNodeGrades = currentNode["grades"];
    const updatedGrades = _.unionBy(itemsList, currentNodeGrades, "id");

    if (_.size(updatedGrades) > _.size(currentNodeGrades)) {
      currentNode["grades"] = [...updatedGrades];

      newNodes[nodeId] = currentNode;
      updatedNodes.push(currentNode);
    }

    newNodes = _addNodeGradesUpwards({
      parentId: currentNodeParentId,
      filterGrades: itemsList,
      nodes: newNodes,
    });
  });
  setNodes(newNodes);
};

export const onBulkRemoveGrades = ({
  nodes,
  setNodes,
  selectedNodeIds,
  itemsList,
}) => {
  let newNodes = { ...nodes };

  _.map(selectedNodeIds, nodeId => {
    const currentNode = { ...newNodes[nodeId] };
    const currentNodeGrades = currentNode.grades;
    const currentNodeChildren = currentNode.children;

    currentNode["grades"] = _.differenceBy(currentNodeGrades, itemsList, "id");
    newNodes[nodeId] = currentNode;

    const currentNodeNewGrades = currentNode["grades"];

    if (_.size(currentNodeNewGrades) < _.size(currentNodeGrades)) {
      newNodes = _removeNodeGradesDownwards({
        children: currentNodeChildren,
        filterGrades: currentNodeNewGrades,
        nodes: newNodes,
      });
    }
  });

  setNodes(newNodes);
};

export const onAddTags = ({ nodes, setNodes, selectedNodeIds, itemsList }) => {
  const updatedNodes = [];

  for (const id of selectedNodeIds) {
    const node = { ...nodes[id] };
    const nodeTags = [...node.tags];
    nodeTags.push(...itemsList);

    const uniqueTags = [
      ...new Map(_.map(nodeTags, tag => [tag.id, tag])).values(),
    ];
    node.tags = uniqueTags;

    updatedNodes.push(node);
  }

  setNodes(prevNodes => {
    return {
      ...prevNodes,
      ..._.keyBy(updatedNodes, "id"),
    };
  });
};

export const onBulkAddTags = ({
  nodes,
  setNodes,
  selectedNodeIds,
  itemsList,
}) => {
  const updatedNodes = [];
  for (const id of selectedNodeIds) {
    const node = { ...nodes[id] };
    const nodeTags = [...node.tags];
    nodeTags.push(...itemsList);

    const uniqueTags = [
      ...new Map(_.map(nodeTags, tag => [tag.id, tag])).values(),
    ];
    node.tags = uniqueTags;
    updatedNodes.push(node);
  }

  setNodes(prevNodes => {
    return {
      ...prevNodes,
      ..._.keyBy(updatedNodes, "id"),
    };
  });
};

export const onBulkRemoveTags = ({
  nodes,
  setNodes,
  selectedNodeIds,
  itemsList,
}) => {
  const updatedNodes = [];

  const removedTagIds = _.map(itemsList, "id");
  for (const id of selectedNodeIds) {
    const node = { ...nodes[id] };

    node.tags = _.filter(
      node.tags,
      tag => !_.includes(removedTagIds, _.get(tag, "id", ""))
    );

    updatedNodes.push(node);
  }

  setNodes(prevNodes => {
    return {
      ...prevNodes,
      ..._.keyBy(updatedNodes, "id"),
    };
  });
};

const _getAllAncestorsOfNode = ({ nodes, node }) => {
  const parents = [];
  const { parentId } = node;
  const parentNode = nodes[parentId];

  if (parentNode) {
    parents.push(parentNode.id);
    parents.push(..._getAllAncestorsOfNode({ nodes, node: parentNode }));
  }
  return parents;
};

const _getAllDescendantsOfNode = ({ nodes, node }) => {
  const descendants = [];

  const children = node.children ?? [];

  for (const childId of children) {
    const childNode = nodes[childId];

    if (childNode) {
      descendants.push(childNode.id);
      descendants.push(..._getAllDescendantsOfNode({ nodes, node: childNode }));
    }
  }

  return descendants;
};

/**
 * When search text is entered, we filter nodes based on search text.
 * We also include all ancestors and descendants of matching nodes.
 */
const _getFilteredNodesBySearch = ({ nodes, searchText }) => {
  const matchingNodes = _.map(
    _.filter(nodes, node => {
      const { label } = node;
      return localSearch({ searchText, text: label }) > -1;
    }),
    "id"
  );

  const allAncestorsOfNode = _.flatMap(matchingNodes, nodeId =>
    _getAllAncestorsOfNode({ nodes, node: nodes[nodeId] })
  );

  const allDescendantsOfNode = _.flatMap(matchingNodes, nodeId =>
    _getAllDescendantsOfNode({ nodes, node: nodes[nodeId] })
  );

  const allMatchingNodes = _.uniq([
    ...matchingNodes,
    ...allAncestorsOfNode,
    ...allDescendantsOfNode,
  ]);

  return _.reduce(
    allMatchingNodes,
    (result, nodeId) => {
      return {
        ...result,
        [nodeId]: nodes[nodeId],
      };
    },
    {}
  );
};

/**
 * When grades are selected, we filter nodes based on selected grades.
 */
const _getFilteredNodesByGrades = ({
  nodes,
  selectedGrades,
  taggedNodeIdsWithAncestors,
}) => {
  const matchingNodes = _.filter(nodes, node => {
    const grades = node.grades ?? [];

    const isGradeAvailable = _.some(grades, ({ id }) =>
      _.includes(selectedGrades, id)
    );

    const isTaggedNode = _.includes(taggedNodeIdsWithAncestors, node.id);

    return isGradeAvailable || isTaggedNode;
  });

  return _.reduce(
    matchingNodes,
    (result, node) => {
      return { ...result, [node.id]: node };
    },
    {}
  );
};

/**
 * When tags are selected, we filter nodes based on selected tags.
 *  We also include all ancestors of matching nodes.
 */
const _getFilteredNodesByTags = ({
  nodes,
  selectedTags,
  taggedNodeIdsWithAncestors,
}) => {
  const matchingNodes = _.map(
    _.filter(nodes, node => {
      const tags = node.tags ?? [];

      const isTagIdFound = _.some(tags, ({ id }) =>
        _.includes(selectedTags, id)
      );

      const isTaggedNode = _.includes(taggedNodeIdsWithAncestors, node.id);

      return isTagIdFound || isTaggedNode;
    }),
    "id"
  );

  const allAncestorsOfNode = _.flatMap(matchingNodes, nodeId =>
    _getAllAncestorsOfNode({ nodes, node: nodes[nodeId] })
  );

  const allMatchingNodes = _.uniq([...matchingNodes, ...allAncestorsOfNode]);

  return _.reduce(
    allMatchingNodes,
    (result, nodeId) => {
      return { ...result, [nodeId]: nodes[nodeId] };
    },
    {}
  );
};

export const getFilteredNodeIds = _.memoize(
  ({ nodes, filters, rootNodeId, taggedNodeIdsWithAncestors }) => {
    const filterKeys = _.keys(filters);

    let filteredNodes = nodes;

    if (
      _.includes(filterKeys, filterIds.searchText) &&
      !_.isEmpty(filters[filterIds.searchText])
    ) {
      filteredNodes = _getFilteredNodesBySearch({
        nodes,
        searchText: filters[filterIds.searchText],
      });
    }

    if (
      _.includes(filterKeys, filterIds.grades) &&
      !_.isEmpty(filters[filterIds.grades])
    ) {
      filteredNodes = _getFilteredNodesByGrades({
        nodes: filteredNodes,
        selectedGrades: filters[filterIds.grades],
        taggedNodeIdsWithAncestors,
      });
    }

    if (
      _.includes(filterKeys, filterIds.tags) &&
      !_.isEmpty(filters[filterIds.tags])
    ) {
      filteredNodes = _getFilteredNodesByTags({
        nodes: filteredNodes,
        selectedTags: filters[filterIds.tags],
        taggedNodeIdsWithAncestors,
      });
    }

    /**
     * We always need to include the root node in the filtered nodes as editor always starts rendering from there.
     */
    const updatedFilteredNodes = {
      ...filteredNodes,
      [rootNodeId]: nodes[rootNodeId],
    };

    return _.mapValues(updatedFilteredNodes, (node, nodeId) => nodeId);
  },
  params => JSON.stringify(params)
);

export const getHTMLLabel = ({ label, searchText, style = {} }) =>
  !searchText || !label
    ? label
    : label.replace(
        createRegex({ text: searchText ?? "", flags: "gi" }),
        val => {
          return ReactDOMServer.renderToStaticMarkup(
            <b style={style}>{val}</b>
          );
        }
      );

export const shouldShowTagCheckbox = ({
  nodeId,
  hasChildren,
  taggedNodesWithAncestors,
  taggedNodeIdsWithAncestors,
}) => {
  if (hasChildren) {
    const node = _.find(taggedNodesWithAncestors, ({ id }) => id == nodeId);
    const nodeChildren = _.get(node, "children", []);
    const isChildSelected = _.size(
      _.intersection(nodeChildren, taggedNodeIdsWithAncestors)
    );
    return _.includes(taggedNodeIdsWithAncestors, nodeId) && !isChildSelected;
  }

  return true;
};

export const getLineHeightFromDepth = depth => {
  const lineHeightsByDepth = { 1: 2.4, 2: 2.2, 3: 2, 4: 1.8, 5: 1.8 };
  return _.get(lineHeightsByDepth, depth, 1.8);
};
